/**
 * Browser copy of api/src/vans/oioxml.ts encodeOioxmlPayload /
 * oioxmlPayloadEncoding. The web app cannot import the Deno package.
 *
 * Used when a download must write the bytes the XML declaration names.
 * Does not change the content or the declaration.
 */

export type OioxmlEncoding = "iso-8859-1" | "utf-8";

const DEFAULT_OIOXML_ENCODING: OioxmlEncoding = "iso-8859-1";
const DECLARATION_ENCODING = /^<\?xml\s[^?]*?encoding\s*=\s*["']([^"']+)["']/;

export function assertLatin1(value: string, field: string): void {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code > 255) {
      throw new Error(`${field} is not ISO-8859-1: code point ${code}`);
    }
  }
}

/** Read the character set the Emessage declares. */
export function oioxmlPayloadEncoding(payload: string): OioxmlEncoding {
  const match = DECLARATION_ENCODING.exec(payload);
  if (!match) return DEFAULT_OIOXML_ENCODING;
  const label = match[1]!.trim().toLowerCase();
  if (label === "iso-8859-1" || label === "iso8859-1" || label === "latin1") {
    return "iso-8859-1";
  }
  if (label === "utf-8" || label === "utf8") return "utf-8";
  throw new Error(`Unsupported Emessage declaration encoding: ${match[1]}`);
}

/** File / wire bytes of the Emessage, matching the declaration. */
export function encodeOioxmlPayload(
  payload: string,
  encoding: OioxmlEncoding = oioxmlPayloadEncoding(payload),
): Uint8Array {
  switch (encoding) {
    case "iso-8859-1": {
      assertLatin1(payload, "Emessage");
      const bytes = new Uint8Array(payload.length);
      for (let i = 0; i < payload.length; i++) bytes[i] = payload.charCodeAt(i);
      return bytes;
    }
    case "utf-8":
      return new TextEncoder().encode(payload);
    default: {
      const unsupported: never = encoding;
      throw new Error(`Unsupported Emessage encoding: ${unsupported}`);
    }
  }
}

/** Blob whose bytes follow the XML declaration (not a UTF-8 re-encode of the string). */
export function xmlDownloadBlob(payload: string, type = "application/xml"): Blob {
  const encoded = encodeOioxmlPayload(payload);
  const bytes = new Uint8Array(encoded.byteLength);
  bytes.set(encoded);
  return new Blob([bytes], { type });
}
